import api from '@/shared/api';
import type { ApiResponse, BulkDeleteResult, Pagination } from '@/shared/lib';

export type MatrixSearchType = 'title' | 'registro';

export interface MatrixResponse {
  id: number;
  prefix: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export type DraftMatrixSortField = 'updatedAt' | 'title';

export interface ListMatricesParams {
  page: number;
  itemsPerPage: number;
  term?: string;
  searchType?: MatrixSearchType;
  sort?: DraftMatrixSortField;
  order?: 'asc' | 'desc';
  updatedAt?: string;
  createdBy?: number;
}

interface PaginatedEnvelope<T> {
  data: T[];
  metadata: Pagination;
}

export async function listMatrices(
  params: ListMatricesParams
): Promise<PaginatedEnvelope<MatrixResponse>> {
  const response = await api.get<PaginatedEnvelope<MatrixResponse>>('/matrices', { params });
  return { data: response.data.data, metadata: response.data.metadata };
}

export interface RejectedMatrixResponse {
  id: number;
  code: string;
  title: string;
  revision: number | null;
  rejectedAt: string;
}

export type RejectedMatrixSortField = 'rejectedAt' | 'title';

export interface ListRejectedMatricesParams {
  page: number;
  itemsPerPage: number;
  term?: string;
  searchType?: MatrixSearchType;
  sort?: RejectedMatrixSortField;
  order?: 'asc' | 'desc';
  rejectedAt?: string;
  createdBy?: number;
  revision?: number;
}

export async function listRejectedMatrices(
  params: ListRejectedMatricesParams
): Promise<PaginatedEnvelope<RejectedMatrixResponse>> {
  const response = await api.get<PaginatedEnvelope<RejectedMatrixResponse>>('/matrices/rejected', {
    params,
  });
  return { data: response.data.data, metadata: response.data.metadata };
}

export async function countRejectedMatrices(): Promise<number> {
  const response = await api.get<{ data: { total: number } }>('/matrices/rejected/count');
  return response.data.data.total;
}

export type RejectedMatrixStage = 'APPROVAL' | 'VERIFICATION';

export interface RejectedMatrixMember {
  name: string;
  registrationNumber: string;
}

export interface RejectedMatrixDecision {
  member: RejectedMatrixMember;
  date: string | null;
}

export interface RejectedMatrixDetailResponse {
  id: number;
  code: string;
  title: string;
  revision: number | null;
  requestedAt: string;
  elaboration: RejectedMatrixMember | null;
  changelog: string[];
  rejectedStage: RejectedMatrixStage;
  approval: RejectedMatrixDecision | null;
  verification: RejectedMatrixDecision | null;
  justification: string | null;
}

export async function getRejectedMatrix(id: number): Promise<RejectedMatrixDetailResponse> {
  const response = await api.get<{ data: RejectedMatrixDetailResponse }>(
    `/matrices/rejected/${id}`
  );
  return response.data.data;
}

export interface RenamedDraftResponse {
  id: number;
  title: string;
  updatedAt: string;
}

export async function renameDraft(id: number, title: string): Promise<RenamedDraftResponse> {
  const response = await api.patch<{ data: RenamedDraftResponse }>(`/matrices/${id}/title`, {
    title,
  });
  return response.data.data;
}

export async function deleteDraft(id: number): Promise<void> {
  await api.delete(`/matrices/${id}`, { params: { draftOnly: true } });
}

export async function deleteDrafts(ids: number[]): Promise<{ data: BulkDeleteResult }> {
  const response = await api.delete<ApiResponse<BulkDeleteResult>>('/matrices/bulk', {
    data: { ids, draftOnly: true },
  });

  return { data: response.data.data! };
}
